/**
 * Browser photo conversion: any supported format → JPEG base64 for /api/kiosk/album/upload.
 * - Standard (JPEG, PNG, TIFF, BMP, GIF): Jimp
 * - HEIC/HEIF: heic2any (dynamic import)
 * - RAW: not supported in browser (libraw.js is Node-only); throws clear error
 */

import { Jimp } from 'jimp';
import { Buffer } from 'buffer';

const RAW_EXTENSIONS = ['cr2', 'cr3', 'nef', 'arw', 'dng', 'raf', 'orf', 'rw2', 'raw'];
const HEIC_EXTENSIONS = ['heic', 'heif'];

const MB = 1024 * 1024;

const MIME_JPEG = 'image/jpeg';

/** Long-edge cap for album-style uploads (pass-through downscale + convertToJpeg). */
export const ALBUM_UPLOAD_MAX_EDGE_PX = 1600;

/** jpeg-js defaults to 512MB decoded budget; large megapixel JPEGs can exceed it. */
const JPEG_DECODE_MAX_MEMORY_MB = 2048;

const jimpJpegDecodeOptions = {
   'image/jpeg': { maxMemoryUsageInMB: JPEG_DECODE_MAX_MEMORY_MB },
} as const;

type JimpImage = Awaited<ReturnType<typeof Jimp.fromBuffer>>;

export type CompressionSettings = {
   maxEdge: number;
   quality: number; // 0-100 for Jimp
};

/**
 * Returns resize + quality based on file size. Used for standard images and pass-through downscale.
 * All tiers use the same long-edge cap; quality scales with file size.
 * < 2MB → 1600px / q92 · 2–8MB → 1600px / q85 · > 8MB → 1600px / q80
 */
export function getCompressionSettings(fileSizeBytes: number): CompressionSettings {
   if (fileSizeBytes < 2 * MB) return { maxEdge: ALBUM_UPLOAD_MAX_EDGE_PX, quality: 92 };
   if (fileSizeBytes < 8 * MB) return { maxEdge: ALBUM_UPLOAD_MAX_EDGE_PX, quality: 85 };
   return { maxEdge: ALBUM_UPLOAD_MAX_EDGE_PX, quality: 80 };
}

export type ConvertResult = {
   base64: string; // data:image/jpeg;base64,...
   width: number;
   height: number;
   originalSizeMB: number;
   settings: CompressionSettings;
};

export type ProgressCallback = (stage: string, pct: number) => void;

/** Browser-safe: turn getBuffer() result (Buffer or Uint8Array) into data:image/jpeg;base64,... */
function bufferToDataUrl(
   buf: ArrayBuffer | Uint8Array | { toString(enc: string): string },
): string {
   if (typeof Buffer !== 'undefined' && Buffer.isBuffer(buf)) {
      return 'data:image/jpeg;base64,' + (buf as Buffer).toString('base64');
   }
   const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf as ArrayBuffer);
   const chunk = 8192;
   let binary = '';
   for (let i = 0; i < bytes.length; i += chunk) {
      const sub = bytes.subarray(i, Math.min(i + chunk, bytes.length));
      binary += String.fromCharCode.apply(null, sub as unknown as number[]);
   }
   return 'data:image/jpeg;base64,' + btoa(binary);
}

function outputFileNameForDownscale(originalName: string, outMime: string): string {
   if (outMime === MIME_JPEG) {
      return originalName.replace(/\.[^.]+$/i, '.jpg') || 'photo.jpg';
   }
   return originalName;
}

/** Normalize Jimp `getBuffer` output for `File` (avoid SharedArrayBuffer typing issues). */
function toBlobPart(buf: ArrayBuffer | Uint8Array): BlobPart {
   const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
   const copy = new Uint8Array(u8.length);
   copy.set(u8);
   return copy;
}

/**
 * Decodes a raster image, downscales if long edge exceeds {@link getCompressionSettings}, and re-encodes.
 * If already within max edge, returns a `File` wrapping the original bytes (no re-encode).
 * WebP/BMP/TIFF use JPEG output (browser Jimp has no WebP encoder; smaller uploads for bmp/tiff).
 */
export async function downscaleRasterIfOverMaxEdge(file: File, mime: string): Promise<File> {
   const normalized =
      mime.toLowerCase() === 'image/jpg' ? MIME_JPEG : mime.toLowerCase();
   const settings = getCompressionSettings(file.size);

   const arrayBuffer = await file.arrayBuffer();
   let image: JimpImage;
   try {
      image = await Jimp.fromBuffer(arrayBuffer, jimpJpegDecodeOptions);
   } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`Failed to load image: ${msg}`);
   }
   if (!image?.bitmap) {
      throw new Error('Image could not be decoded. Try a different format or size.');
   }
   const { width, height } = image.bitmap;
   if (typeof width !== 'number' || typeof height !== 'number') {
      throw new Error('Image has no dimensions. The file may be corrupted.');
   }

   if (Math.max(width, height) <= settings.maxEdge) {
      return new File([file], file.name, {
         type: file.type || normalized,
         lastModified: file.lastModified,
      });
   }

   const ratio = settings.maxEdge / Math.max(width, height);
   image.resize({ w: Math.round(width * ratio), h: Math.round(height * ratio) });

   let outMime: string;
   let outName = file.name;

   if (normalized === MIME_JPEG) {
      outMime = MIME_JPEG;
      outName = outputFileNameForDownscale(file.name, MIME_JPEG);
   } else if (normalized === 'image/png') {
      outMime = 'image/png';
   } else if (
      normalized === 'image/webp' ||
      normalized === 'image/bmp' ||
      normalized === 'image/x-ms-bmp' ||
      normalized === 'image/tiff'
   ) {
      // Browser Jimp: no WebP encoder; JPEG is smaller for bmp/tiff after resize.
      outMime = MIME_JPEG;
      outName = outputFileNameForDownscale(file.name, MIME_JPEG);
   } else {
      outMime = MIME_JPEG;
      outName = outputFileNameForDownscale(file.name, MIME_JPEG);
   }

   const buf =
      outMime === MIME_JPEG
         ? await image.getBuffer(MIME_JPEG, { quality: settings.quality })
         : await image.getBuffer('image/png');

   return new File([toBlobPart(buf)], outName, {
      type: outMime,
      lastModified: Date.now(),
   });
}

/**
 * Converts any supported photo file to JPEG base64. Routes by extension.
 */
export async function convertToJpeg(
   file: File,
   onProgress?: ProgressCallback,
): Promise<ConvertResult> {
   const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
   if (RAW_EXTENSIONS.includes(ext)) return convertRaw(file, onProgress);
   if (HEIC_EXTENSIONS.includes(ext)) return convertHeic(file, onProgress);
   return convertStandard(file, onProgress);
}

async function convertStandard(file: File, onProgress?: ProgressCallback): Promise<ConvertResult> {
   const settings = getCompressionSettings(file.size);

   onProgress?.('Reading', 10);
   const arrayBuffer = await file.arrayBuffer();

   onProgress?.('Converting', 35);
   let image: JimpImage;
   try {
      image = await Jimp.fromBuffer(arrayBuffer, jimpJpegDecodeOptions);
   } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`Failed to load image: ${msg}`);
   }
   if (!image?.bitmap) {
      throw new Error('Image could not be decoded. Try a different format or size.');
   }
   const { width, height } = image.bitmap;
   if (typeof width !== 'number' || typeof height !== 'number') {
      throw new Error('Image has no dimensions. The file may be corrupted.');
   }

   onProgress?.('Resizing', 65);
   if (Math.max(width, height) > settings.maxEdge) {
      const ratio = settings.maxEdge / Math.max(width, height);
      image.resize({ w: Math.round(width * ratio), h: Math.round(height * ratio) });
   }

   onProgress?.('Encoding', 85);
   const buf = await image.getBuffer(MIME_JPEG, { quality: settings.quality });
   const base64 = bufferToDataUrl(buf);

   onProgress?.('Done', 100);
   return {
      base64,
      width: image.bitmap.width,
      height: image.bitmap.height,
      originalSizeMB: file.size / MB,
      settings,
   };
}

async function convertRaw(_file: File, onProgress?: ProgressCallback): Promise<ConvertResult> {
   onProgress?.('Loading RAW decoder', 5);
   throw new Error(
      'RAW files (CR2, CR3, NEF, ARW, etc.) are not supported in the browser. Please upload from the desktop app or use JPEG/HEIC.',
   );
}

async function convertHeic(file: File, onProgress?: ProgressCallback): Promise<ConvertResult> {
   const settings = getCompressionSettings(file.size);

   onProgress?.('Decoding HEIC', 15);
   const heic2any = (await import('heic2any')).default;
   const result = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: settings.quality / 100,
   });
   const jpegBlob = Array.isArray(result) ? result[0] : result;
   if (!jpegBlob || !(jpegBlob instanceof Blob)) {
      throw new Error('HEIC conversion did not return a blob');
   }

   onProgress?.('Resizing', 60);
   const ab = await jpegBlob.arrayBuffer();
   let image: JimpImage;
   try {
      image = await Jimp.fromBuffer(ab, jimpJpegDecodeOptions);
   } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`Failed to load HEIC-converted image: ${msg}`);
   }
   if (!image?.bitmap) {
      throw new Error('Failed to load HEIC-converted image.');
   }
   const { width, height } = image.bitmap;
   if (Math.max(width, height) > settings.maxEdge) {
      const ratio = settings.maxEdge / Math.max(width, height);
      image.resize({ w: Math.round(width * ratio), h: Math.round(height * ratio) });
   }

   onProgress?.('Encoding', 85);
   const buf = await image.getBuffer(MIME_JPEG, { quality: settings.quality });
   const base64 = bufferToDataUrl(buf);

   onProgress?.('Done', 100);
   return {
      base64,
      width: image.bitmap.width,
      height: image.bitmap.height,
      originalSizeMB: file.size / MB,
      settings,
   };
}
